import { useSearchParams } from 'react-router-dom';

/**
 * URL-backed multi-select filter state. Reads and writes a single
 * comma-separated query parameter (e.g. `?owner=group:default/a,group:default/b`)
 * so list-view filter selections are shareable and survive refreshes.
 *
 * @param param - The query-parameter name to read/write.
 */
export const useUrlMultiSelect = (
  param: string,
): [string[], (values: string[]) => void] => {
  const [searchParams, setSearchParams] = useSearchParams();

  const values = Array.from(
    new Set(
      (searchParams.get(param) ?? '')
        .split(',')
        .map(value => value.trim())
        .filter(Boolean),
    ),
  );

  const setValues = (next: string[]) => {
    setSearchParams(
      prev => {
        const nextParams = new URLSearchParams(prev);
        const unique = Array.from(new Set(next.filter(Boolean)));
        if (unique.length === 0) {
          nextParams.delete(param);
        } else {
          nextParams.set(param, unique.join(','));
        }
        return nextParams;
      },
      { replace: true },
    );
  };

  return [values, setValues];
};

/**
 * Returns a function that removes several query parameters in a single
 * navigation.
 */
export const useResetUrlParams = (): ((params: string[]) => void) => {
  const [, setSearchParams] = useSearchParams();

  return (params: string[]) => {
    setSearchParams(
      prev => {
        const nextParams = new URLSearchParams(prev);
        for (const param of params) {
          nextParams.delete(param);
        }
        return nextParams;
      },
      { replace: true },
    );
  };
};
